// dsParam.js
// @author: anghello

/**
 * dsParam.js
 *
 * Purpose:
 * - Create and persist a stable user identifiable token.
 * - Build the `ds` query value in the format: `CCC-APP-YYYYMMDD-HHMMSS-XXXXXX`.
 *
 * How it works:
 * 1) `getUserIdentifiable()` checks an in-memory cache first.
 * 2) If missing, it tries `localStorage` using `STORAGE_KEY`.
 * 3) If still missing, it creates a new value:
 *    - UTC timestamp: `YYYYMMDD-HHMMSS`
 *    - Random 6-char alphanumeric suffix
 * 4) It stores the value in memory and `localStorage`.
 * 5) `buildDsParam(app, client)` returns `CLIENT-APP-userIdentifiable`
 *    (`client` is normalized to uppercase).
 *
 * Sanitization:
 * - `client` is sanitized to letters only and truncated to 1..5 chars.
 * - `app` is sanitized to `[A-Za-z0-9_]` only and truncated to 1..10 chars.
 * - Before returning, `ds` is validated against the same regex used by the PHP server.
 *
 * Integration steps (copy to other projects):
 * 1) Copy this file.
 * 2) Import `buildDsParam` where you build request URLs.
 * 3) Build once per service:
 *    `const ds = buildDsParam(APP_NAME, APP_CLIENT);`
 * 4) Append to URL query:
 *    `&ds=${encodeURIComponent(ds)}`
 * 5) Keep a consistent `STORAGE_KEY` if you want persistence across sessions.
 *
 * Notes:
 * - If `localStorage` is unavailable, this still works with in-memory fallback.
 * - If `app` or `client` is empty, `buildDsParam` returns an empty string.
 */

const STORAGE_KEY = 'pz_user_identifiable_slinks';
const CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

let userIdentifiableCache = null;

function pad2(value) {
  return String(value).padStart(2, '0');
}

function formatUtcDate(date = new Date()) {
  const year = date.getUTCFullYear();
  const month = pad2(date.getUTCMonth() + 1);
  const day = pad2(date.getUTCDate());
  const hour = pad2(date.getUTCHours());
  const minute = pad2(date.getUTCMinutes());
  const second = pad2(date.getUTCSeconds());
  return `${year}${month}${day}-${hour}${minute}${second}`;
}

function generateRandomSuffix(length = 6) {
  // Match Swift behavior: shuffle a pool and take first N (no repeats).
  const arr = CHARS.split('');
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr.slice(0, length).join('');
}

function persistUserIdentifiable(value) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, value);
    }
  } catch (_) {
    // localStorage can be blocked; keep memory fallback.
  }
}

function readStoredUserIdentifiable() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem(STORAGE_KEY);
    }
  } catch (_) {
    // localStorage can be blocked; return null.
  }
  return null;
}

export function getUserIdentifiable() {
  if (userIdentifiableCache) {
    return userIdentifiableCache;
  }

  const storedValue = readStoredUserIdentifiable();
  if (storedValue) {
    userIdentifiableCache = storedValue;
    return userIdentifiableCache;
  }

  const date = formatUtcDate(new Date());
  const uniqueID = generateRandomSuffix(6);
  userIdentifiableCache = `${date}-${uniqueID}`;
  persistUserIdentifiable(userIdentifiableCache);

  return userIdentifiableCache;
}

export function buildDsParam(app, client) {
  // PHP validation expects:
  //  - client: letters only, 1..5
  //  - app: [A-Za-z0-9_] only, 1..10
  const safeAppRaw = (app || '').toString().trim();
  const safeClientRaw = (client || '').toString().trim().toUpperCase();

  const safeClient = safeClientRaw.replace(/[^A-Za-z]/g, '').slice(0, 5);
  const safeApp = safeAppRaw.replace(/[^A-Za-z0-9_]/g, '').slice(0, 10);

  if (!safeApp || !safeClient) {
    return '';
  }

  const userIdentifiable = getUserIdentifiable();
  const ds = `${safeClient}-${safeApp}-${userIdentifiable}`;

  // Validate against PHP regex to avoid sending invalid values.
  const dsValidationPattern = /^[A-Za-z]{1,5}-[A-Za-z0-9_]{1,10}-\d{8}-\d{6}-[A-Za-z0-9]{6}$/;
  if (!dsValidationPattern.test(ds)) {
    return '';
  }

  return ds;
}
